import {Layers, RockBottomLayers, SimpleStageLayer} from "./RockBottomLayers";
import {SpinePlayer} from "../../core/utils/SpinePlayer";
import {RockBottomGameConfig} from "../RockBottomGameConfig";
import {gsap, TimelineLite, TweenLite} from "gsap";
import {RockBottomReelView} from "../reels/RockBottomReelView";
import {RockBottom} from "../RockBottom";
import {RockBottomSymbol} from "../symbol/RockBottomSymbol";
import {RockBottomSound} from "../sound/RockBottomSound";
import {RockBottomParsedGameData} from "../server/data/RockBottomParsedGameData";
import {RockBottomParsedInitData} from "../server/data/RockBottomParsedInitData";
import {RockBottomGameMode} from "../server/RockBottomGameMode";
import {MathHelper} from "../../core/utils/MathHelper";
import {RockBottomReelController} from "../reels/RockBottomReelController";

export class RockBottomMidSymExpandView extends PIXI.Container {
    private _highlightContainer: PIXI.Container[] = [];
    private _animContainer: PIXI.Container[] = [];
    private _wildContainer: PIXI.Container[] = [];
    private _layer: SimpleStageLayer | undefined;
    private _activeSymbolId: number | null;
    private _expandSymbolCount: number = 0;
    private _mainTl: TimelineLite[] = [];

    constructor() {
        super();
        this.init();
    }

    //#285
    private static shallSkipAnims(): boolean {
        let flag: boolean = false;

        const parsedGameData: RockBottomParsedGameData = RockBottom.model.parsedGameData,
            nearWinReels: boolean[] = parsedGameData.nearWinReels;

        if (!(nearWinReels.includes(true))) {
            flag = true;
        }

        return flag;
    }

    public getActiveSymbolId(): number | null {
        return this._activeSymbolId;
    }

    public getActiveSymbolName(): string | null {
        const activeSymIndex: number | null = this.getActiveSymbolId();
        if (activeSymIndex !== null) {
            return RockBottomGameConfig.MID_SYMS[activeSymIndex];
        } else {
            return null;
        }
    }

    public resetActiveSymbolId(): void {
        this._activeSymbolId = null;
    }

    public setActiveSymbol(symId: number): void {
        if (this._activeSymbolId !== symId) {
            this._activeSymbolId = symId;
        }
    }

    public playExpandAnim(reelId: number, index: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite(),
            activeSymIndex: number | null = this.getActiveSymbolId();

        if (activeSymIndex !== null && index === activeSymIndex) {
            const parsedData: RockBottomParsedGameData | RockBottomParsedInitData = RockBottom.getParsedData(),
                isSFS: RockBottomGameMode = parsedData.mode === RockBottomGameMode.SUPER_FREESPIN,
                highlightAnim: SpinePlayer = (this._highlightContainer[reelId - 1].children[0]) as SpinePlayer,
                expandAnim: SpinePlayer = (this._animContainer[reelId - 1].children[index]) as SpinePlayer,
                wildAnim: SpinePlayer = (this._wildContainer[reelId - 1].children[index]) as SpinePlayer,
                symId: number = (RockBottom.model.parsedGameData.reels[reelId].slice(1, -1)).indexOf(RockBottomGameConfig.MID_SYMS[index]),
                symbol: RockBottomSymbol = RockBottom.reels[reelId].getSymbol(symId),
                symPos: PIXI.Point = symbol.getGlobalPosition(),
                reelController: RockBottomReelController = RockBottom.reels[reelId],
                isQuickStop: boolean = reelController.isQuickStop,
                shallSkip: boolean = RockBottomMidSymExpandView.shallSkipAnims();

            let randomNo: number = MathHelper.uniqueNumberInRange(0, 1),
                soundNo: number = parsedData.fsLevel;

            this._expandSymbolCount++;

            tl.add(() => {
                highlightAnim.position.set(0, symPos.y);
            });

            if (!shallSkip && !isQuickStop) {
                tl.add(highlightAnim.play({
                    name: symbol.symbolName,
                    loop: false,
                    onStart: () => {
                        highlightAnim.alpha = 1;
                        if (!isQuickStop) {
                            RockBottomSound.soundPlayer.playTrack({name: RockBottomSound.BONUS_GAME_EXPANDING_DOWN});
                        }
                    }
                }));
            }

            if (!(isQuickStop && isSFS)) {
                tl.add(expandAnim.play({
                    name: (isQuickStop || shallSkip) ? "Expand_static" : "Expand",
                    onStart: () => {
                        expandAnim.alpha = 1;
                        if (isQuickStop || shallSkip) {
                            if (isSFS) {
                                expandAnim.stop();
                            } else {
                                expandAnim.fastStop();
                            }
                        } else {
                            if (this._expandSymbolCount === 1) {
                                RockBottomSound.soundPlayer.playTrack({name: RockBottomSound.BONUS_GAME_EXPANDING_SYMBOL_REEL1[soundNo]});
                            } else if (this._expandSymbolCount === 2) {
                                if (reelId == 2) {
                                    RockBottomSound.soundPlayer.playTrack({name: RockBottomSound.BONUS_GAME_EXPANDING_SYMBOL_REEL2[soundNo]});
                                }
                                if (reelId == 3) {
                                    RockBottomSound.soundPlayer.playTrack({name: RockBottomSound.BONUS_GAME_EXPANDING_SYMBOL_REEL3[soundNo]});
                                }
                            } else if (this._expandSymbolCount === 3) {
                                RockBottomSound.soundPlayer.playTrack({name: RockBottomSound.BONUS_GAME_EXPANDING_SYMBOL_REEL3[soundNo]});
                            }
                            RockBottomSound.soundPlayer.playTrack({
                                name: RockBottomSound.BONUS_GAME_EXPANDING_UP,
                                volume: 0.5
                            });
                        }
                    }
                }));
            }

            let activeSymName: string | null = this.getActiveSymbolName();
            if (isSFS && activeSymName) {
                const wildAnimName: string = activeSymName + (isQuickStop ? "_Win_static" : "_Win");

                tl.add(wildAnim.play({
                    name: wildAnimName,
                    loop: false,
                    onStart: () => {
                        wildAnim.alpha = 1;
                        if (isQuickStop || shallSkip) {
                            wildAnim.fastStop();
                        } else {
                            RockBottomSound.soundPlayer.playTrack({name: RockBottomSound.SFS_WILD_GLITCH[randomNo]});
                        }
                    }
                }));
            }

            if (parsedData.betWins.length === 0) {
                tl.add(TweenLite.to(this, {duration: 0.3}));
            }

            if (isQuickStop) {
                for (let i: number = 0; i < this._mainTl.length; i++) {
                    this._mainTl[i].timeScale(1000);
                }
            } else {
                this._mainTl.push(tl);
            }
        }

        return tl;
    }

    public playWinAnimation(reelId: number, symId: number, symName: string): void {
        if (RockBottom.inBonusGame) {
            if (symName === this.getActiveSymbolName()) {
                const parsedGameData: RockBottomParsedGameData = RockBottom.model.parsedGameData,
                    isFS: boolean = parsedGameData.mode === RockBottomGameMode.FREESPIN,
                    index: number = RockBottomGameConfig.MID_SYMS.indexOf(symName),
                    expandAnim: SpinePlayer = (this._animContainer[reelId - 1].children[index]) as SpinePlayer,
                    wildAnim: SpinePlayer = (this._wildContainer[reelId - 1].children[index]) as SpinePlayer;

                if (isFS) {
                    expandAnim.play({
                        name: "Expand_Win"
                    });
                } else {
                    wildAnim.play({
                        name: symName + "_Win_Pres"
                    });
                }
            }
        }
    }

    public removeExpandAnims(): void {
        if (RockBottom.inBonusGame) {
            for (let i: number = 0; i < this._animContainer.length; i++) {
                this.removeAnimation(this._highlightContainer[i]);
                this.removeAnimation(this._animContainer[i]);
                this.removeAnimation(this._wildContainer[i]);
            }
            this.resetMainTimeLine();
        }
        this._expandSymbolCount = 0;
    }

    private resetMainTimeLine(): void {
        if (this._mainTl.length > 0) {
            for (let i: number = 0; i < this._mainTl.length; i++) {
                // @ts-ignore
                if (this._mainTl[i] && this._mainTl[i].isActive()) {
                    this._mainTl[i].paused();
                    this._mainTl[i].kill();
                }
            }
            this._mainTl = [];
        }
    }

    private removeAnimation(animContainer: PIXI.Container): void {
        (<SpinePlayer[]>animContainer.children).forEach((spineAnim: SpinePlayer) => {
            const tl: gsap.core.Timeline = gsap.timeline();
            tl.to(spineAnim, {
                alpha: 0,
                duration: 0.3,
                onComplete: () => {
                    spineAnim.stop();
                }
            });
        });
    }

    private init(): void {
        this._layer = RockBottomLayers.getLayer(Layers.MID_SYM_EXPAND);
        this.createSpineContainers();
        this._layer?.addChild(this);
    }

    private createSpineContainers(): void {
        for (let reelId: number = 1; reelId < 4; reelId++) {
            this._highlightContainer.push(this.createContainer(reelId, "highlight"));
            this._highlightContainer[reelId - 1].addChild(new SpinePlayer("Symbol_Highlight"));
            this._animContainer.push(this.createContainer(reelId, "expand"));
            this._wildContainer.push(this.createContainer(reelId, "wild"));
            for (let j: number = 0; j < RockBottomGameConfig.MID_SYMS.length; j++) {
                const midSym = "M" + (j + 1);
                this._animContainer[reelId - 1].addChild(new SpinePlayer(midSym));
                this._wildContainer[reelId - 1].addChild(new SpinePlayer("W_Reveal"));
            }
            this.addChild(this._highlightContainer[reelId - 1], this._animContainer[reelId - 1], this._wildContainer[reelId - 1]);
        }
    }

    private createContainer(reelId: number, name: string): PIXI.Container {
        const wrapper: PIXI.Container = new PIXI.Container(),
            posX: number = RockBottomReelView.REEL_AREAS[reelId].x;

        wrapper.position.set(posX, 360);
        if (name === "highlight") {
            wrapper.position.set(posX, 0);
        }
        wrapper.name = name + "_" + reelId;

        return wrapper;
    }
}